import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { media, Container, Heading, Input, Text, ExternalLink } from '../ui-components';
import COMMON from '../static-content/common.json';
import Video from './video';
import { switchContactForm } from '../utils/switch-contact-form';
import { initiateRedirectInNewTab, initiateRedirectAsAssign } from '../utils/initiate-redirect';
import { gaDownloadEvent } from '../utils/ga-custom-event';

const propTypes = {
  requireForm: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.string,
  attachment: PropTypes.oneOfType([
    PropTypes.shape({
      contentType: PropTypes.string,
      fileName: PropTypes.string,
      url: PropTypes.string
    }),
    PropTypes.string
  ]).isRequired,
  completedFormPrompt: PropTypes.string,
  insightTitle: PropTypes.string,
  geoSpace: PropTypes.string,
  slug: PropTypes.string,
  redirectContentUrl: PropTypes.string
};

/**
 * Component that renders optionally gated content, either a download or a video depending on attachment
 *
 * @component
 * @param props
 * @prop {string} requireForm
 * @prop {string} title
 * @prop {string} type
 * @prop {any} attachment
 * @prop {bool} completedFormPrompt
 * @prop {string} insightTitle
 * @prop {string} geoSpace
 * @prop {string} slug
 */

const LegacyGatedContent = props => {
  const { requireForm, title, type, attachment, completedFormPrompt, geoSpace, slug, redirectContentUrl, customForm } =
    props;

  const [formComplete, setFormComplete] = useState(false);
  const [redirectComplete, setRedirectComplete] = useState(false);

  useEffect(() => {
    if (redirectContentUrl && (!title || !completedFormPrompt)) {
      setFormComplete(true);
    }
  }, [redirectContentUrl, title, completedFormPrompt]);

  const downloadAttachment = () => {
    gaDownloadEvent(attachment?.fileName);

    fetch(attachment.url).then(response => {
      response.blob().then(blob => {
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = attachment.fileName;
        a.click();
      });
    });
  };

  const isExternalURL = url => {
    if (typeof window !== 'undefined') {
      return new URL(url).origin !== window.location.origin;
    }
  };

  const initiateRedirect = redirectContentUrl => {
    if (!redirectComplete) {
      setRedirectComplete(true);
      const isExternal = isExternalURL(redirectContentUrl);

      if (isExternal) {
        initiateRedirectInNewTab(redirectContentUrl);
      } else {
        initiateRedirectAsAssign(redirectContentUrl);
      }
    }
  };

  const renderTitleSection = () => {
    const whitepaperText = requireForm
      ? COMMON.GATED_CONTENT_DOWNLOAD_WHITEPAPER__TITLE
      : COMMON.DOWNLOAD_WHITEPAPER__TITLE;
    const insightText = requireForm ? COMMON.GATED_CONTENT_DOWNLOAD_INSIGHT__TITLE : COMMON.DOWNLOAD_INSIGHT__TITLE;
    const videoText = requireForm ? COMMON.GATED_CONTENT_VIDEO__TITLE : COMMON.VIDEO__TITLE;
    const headingText = title
      ? title
      : type === 'Whitepaper'
      ? whitepaperText
      : typeof attachment === 'string'
      ? videoText
      : insightText;

    return (
      <TitleSection>
        <Heading level={2}>{headingText}</Heading>
      </TitleSection>
    );
  };

  /**
   * Renders the form
   *
   * @function renderForm
   * @returns {JSX.Element}
   */
  const renderForm = () => {
    return switchContactForm(customForm, geoSpace, setFormComplete, requireForm, slug, completedFormPrompt);
  };

  /**
   *
   * Renders a prompt upon form completion. If a redirect link is provided,
   * it combines the prompt with default text about redirecting the user.
   *
   * @function renderFormCompleted
   * @returns {JSX.Element}
   */
  const renderFormCompleted = () => {
    if (redirectContentUrl) {
      initiateRedirect(redirectContentUrl);
    } else {
      downloadAttachment();
    }

    return (
      <Prompt>
        <Text>
          {redirectContentUrl ? (
            <Text>
              {completedFormPrompt} If you are not automatically redirected{' '}
              <ExternalLink linkstyle={'secondary-link'} href={redirectContentUrl}>
                click here.
              </ExternalLink>
            </Text>
          ) : (
            completedFormPrompt || 'Thank you for downloading.'
          )}
        </Text>
      </Prompt>
    );
  };

  /**
   * TODO: Design needed.
   * Render a completed/thank you message and render the video once the video form has been completed
   *
   * @function renderVideoFormCompleted
   * @returns {JSX.Element}
   */
  const renderVideoFormCompleted = () => {
    return (
      <>
        <Prompt>
          <Text>{'Thank you for viewing.'}</Text>
        </Prompt>
        {renderVideo()}
      </>
    );
  };

  /**
   * Render download button which allows users to download the content
   *
   * @function renderDownload
   * @returns {JSX.Element}
   */
  const renderDownload = () => {
    return <Input isSubmitType value="Download" onClick={downloadAttachment} />;
  };

  /**
   * Render download button which allows users to download the content
   *
   * @function renderVideo
   * @returns {JSX.Element}
   */
  const renderVideo = () => {
    return <Video embedUrl={attachment} margin={'0px'} />;
  };

  /**
   * Render content including optional form, download button, or video
   *
   * @function renderContent
   * @returns {JSX.Element}
   */
  const renderContent = () => {
    if (attachment?.url) {
      return requireForm ? (formComplete ? renderFormCompleted() : renderForm()) : renderDownload();
    } else if (typeof attachment === 'string') {
      return requireForm ? (formComplete ? renderVideoFormCompleted() : renderForm()) : renderVideo();
    } else if (redirectContentUrl) {
      return requireForm ? (formComplete ? renderFormCompleted() : renderForm()) : null;
    }
  };

  return (
    <ContactContainer sectionContainer>
      {renderTitleSection()}
      {renderContent()}
    </ContactContainer>
  );
};

LegacyGatedContent.propTypes = propTypes;

export default LegacyGatedContent;

const ContactContainer = styled(Container)`
  padding: 0;
  margin: 40px 0;

  @media screen and (min-width: ${media.mediumTop}) {
    margin: 80px 0 0;
  }
`;

const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Prompt = styled.p`
  text-align: center;
`;
